import React, { use, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import useWindowSize from '@hooks/use-window-size';
import { SaleData } from '@interfaces/api/responses/my-sale';
import { useRouter } from 'next/router';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { Popin } from '@components/orders-sales/type';
import { TimelineContext } from '@context/timeline.context';
import { Product } from '@interfaces/models/orderSaleProduct';
import { onImageError, productImage, saleListingId, updateURLQueries } from '@components/orders-sales/helper';
import { ROUTER_QUERY_KEY } from '@hooks/orders-sales/use-query-tab';
import { URL_QUERIES } from '@constants/orders-sales';
import Button from '@components/atoms/button/button';
import useSaleAwaitingShipItems from '@hooks/orders-sales/use-sales-awaiting-ship-items';
import PopinView from '@components/orders-sales/popin';
import styles from '../../main.module.scss';
import GetShippingLabelButton from './get-shipping-label-button';

interface ShippingReminderProps {
  data?: SaleData;
}

const ShippingReminder = ({ data: saleData }: ShippingReminderProps) => {
  const router = useRouter();

  const { isWiderThanMd } = useWindowSize();

  const { sendAnalyticEvent } = useAnalyticEvents('orders_sales_timeline');

  const { t } = useTranslation('orders-sales');

  const { state, updateState } = use(TimelineContext);

  const [popin, updatePopin] = useState<Popin | null>(null);

  const { data: response } = useSaleAwaitingShipItems();

  const isEmpty = !response || !saleData;

  const setTimeline = (shippingProduct: Product) => {
    // Need to scroll the Sale Listing to top in order to view this product in the listing
    if (typeof window !== 'undefined' && document && document.getElementById(saleListingId)) {
      document.getElementById(saleListingId).scrollTop = 0;
    }

    updateState({
      ...state,
      currentTab: 'Sale',
      saleProduct: shippingProduct,
      shippingProduct,
    });

    updateURLQueries(router.pathname, { section: ROUTER_QUERY_KEY.sale });
    sendAnalyticEvent('my_account_send_products', {});
  };

  useEffect(() => {
    const section = new URLSearchParams(window.location.search).get(URL_QUERIES.section);

    if (isEmpty || section !== ROUTER_QUERY_KEY.shippingReminder) {
      return;
    }
    if (response.data?.length) {
      setTimeline(response.data[0]);
    }
    // eslint-disable-next-line
  }, [isEmpty, response]);

  if (isEmpty) {
    return <></>;
  }

  const { data: productsWaitingToShip = [] } = response;

  const product = productsWaitingToShip?.length ? productsWaitingToShip[0] : null;

  if (!product) {
    return <></>;
  }

  const img =
    product && productsWaitingToShip.length === 1 ? (
      <img
        className={styles.ordersSales__shipping__info__image}
        src={productImage(product.imageUrl)}
        alt={product.productName}
        onError={onImageError}
      />
    ) : null;

  const showShippingLabelBtn = productsWaitingToShip?.length === 1;

  const shippingLabelUrl = showShippingLabelBtn ? productsWaitingToShip[0].shippingLabelUrl : undefined;
  const cancelSaleUrl = showShippingLabelBtn ? productsWaitingToShip[0].cancelSaleUrl : undefined;

  return (
    <div className={styles.ordersSales__shipping}>
      <div className={styles.ordersSales__shipping__info}>
        {isWiderThanMd && img}

        <div className={styles.ordersSales__shipping__info__right}>
          <div className={styles.ordersSales__shipping__content}>
            {!isWiderThanMd && img}

            <div className={styles.ordersSales__shipping__info__content}>
              <div className={styles.ordersSales__shipping__info__title}>
                {productsWaitingToShip.length === 1
                  ? t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.TITLE_SINGLE')
                  : t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.TITLE_MULTIPLE')}
              </div>
              <div className={styles.ordersSales__shipping__info__desc}>
                {productsWaitingToShip.length === 1
                  ? t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.DESC_SINGLE')
                  : t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.DESC_MULTIPLE')}
              </div>
            </div>
          </div>

          <div className={styles.ordersSales__shipping__btns}>
            {productsWaitingToShip.length > 1 && (
              <Button
                variant="primary"
                size="medium"
                onClick={() => setTimeline(product)}
              >
                {t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.BTN_SEND_MY')}
                {` ${productsWaitingToShip.length} ${t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.BTN_SOLD_PRODUCTS')}`}
              </Button>
            )}

            <GetShippingLabelButton
              shippingLabelUrl={shippingLabelUrl}
              product={product}
            />

            {!!cancelSaleUrl && (
              <a
                className={clsx('vc-btn vc-btn--medium vc-btn--outline', styles.ordersSales__shipping__btn__cancel)}
                href={cancelSaleUrl}
                onClick={() => {
                  sendAnalyticEvent('my_account_cancel_sale', { product_id: product.productId });
                }}
              >
                {t('MY_ACCOUNT.ORDERS_SALES.SHIPPING.BTN_CANCEL')}
              </a>
            )}
          </div>
        </div>
      </div>

      <PopinView
        popin={popin}
        closePopin={() => updatePopin(null)}
      />
    </div>
  );
};

export default ShippingReminder;
